import { isSelfStatusOrigin } from '../janus-engine/statuses/status-origin.js';
import { ecId, ecLean, HandBack, Refusal, itemField, record, upper, type Row } from '../janus-engine/ability/context.js';
import { planCraftHeartbeat, planCraftMembership, planCraftStart, WIP_SHELL, CRAFT_PROGRESS, type CraftSnapshot } from '../janus-engine/actions/crafting-activity.js';
import { resolveCraftStart, STATUS_CRAFTING, type CraftStartReaders } from '../janus-engine/actions/crafting-start.js';
import { readLayerListEntryUuid } from '../janus-engine/compile/inventory-raw-readers.js';
import { activityPortEnv, statusScalar } from '../janus-engine/actions/activity.js';
import { inventoryIdOf, isUuid, statusEntries, statusItemOf, templateIdOf, walkRows } from '../janus-engine/minigames/rows.js';
import { ftCraftingProgressPayloadParse, ftCraftingWorkerPayloadParse } from '../janus-engine/port/k3-rules/crafting_wip_helpers.js';
import { skipUnported } from '../janus-engine/unported.js';
import { requiredToolIds } from '../janus-engine/minigames/lockpick.js';
import { ftCampaignNowSeconds, ftClockCalendarFromArr, type CampaignClockCols } from '../shared/campaign-clock.js';
import { ensureActorRowHeld, runRowOnly } from '../shared/actor-row-loader.js';
import { certifyMembers } from './environmental-payload-truth.js';
import { actorCanReachTarget } from '../shared/proximity-browser.js';
import { actorEncounterFor, actorRowFor, waitForActorMovement } from './movement-truth.js';
import { fetchAndCacheObject } from './object-cache.js';
import { abilityTruthReaders, fetchObjectsBatch, hydrateReferencedDefinitions } from './ability-truth.js';
import { fetchAndCacheCaster, withSettledCasterReads } from './caster-cache.js';
import { activityContext, activityError, decideActivity, ensureActivityMembers, loadActivityStatus, recoverActivity, saveActivity } from './activity-truth.js';

async function held(actorId: string, encounterId?: string, start?: CraftStartRequest, participantsHeld = false) {
  const location = await ensureActorRowHeld(actorId, encounterId, { what: 'Crafting' });
  if (location.kind !== 'encounter') throw new HandBack('crafting:actor-not-in-encounter');
  await recoverActivity(location.encounterId, actorId);
  await waitForActorMovement(location.encounterId, actorId);
  if (!participantsHeld) await ensureActivityMembers(location.encounterId, [actorId]);
  const caches = window.FateTide as unknown as { casterCache?: Map<string, unknown>; campaignCache?: Map<string, unknown>; currentCampaignArr?: unknown };
  const snapshot = () => ({ actor: record(actorRowFor(location.encounterId, actorId)) ?? record(caches.casterCache?.get(upper(actorId))),
    encounter: record(actorEncounterFor(location.encounterId)) });
  if (start) {
    const gate = await start.gate(location.encounterId);
    if (gate.kind !== 'allow') throw new HandBack(gate.kind === 'refuse' ? gate.message : gate.site);
    await hydrate(start.recipe, start.sizeId ? [start.sizeId] : []);
    await holdCraftStartFacts(start, location.encounterId, snapshot);
  }
  const { actor, encounter } = snapshot();
  if (!actor || !encounter || !start && !Array.isArray(actor.inventory)) throw new HandBack('crafting:carried-tree-not-held');
  const certified = await certifyMembers(location.encounterId);
  if (!certified) throw new HandBack('crafting:reaction-participants-not-held');
  const ctx = activityContext(location.encounterId, actorId, actor, certified.complete);
  const parent = upper(record(encounter.encounters)?.encounter_master_parent_id);
  const campaign = [caches.campaignCache?.get(parent), caches.currentCampaignArr].map(record).find(row => upper(record(row?.campaigns)?.campaign_id ?? row?.campaign_id) === parent);
  const clock = ctx.read.campaignClock(), calendar = ftClockCalendarFromArr(campaign);
  if (!clock || !calendar.months.length) throw new HandBack('crafting:campaign-clock-not-held');
  return { ctx, actor, encounter, encounterId: location.encounterId, calendar, now: ftCampaignNowSeconds(clock, calendar) };
}

async function hydrate(recipe: Row, extras: string[] = []): Promise<void> {
  const refs = JSON.stringify(recipe.item_layer_types ?? {}).match(/[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}/gi) ?? [];
  const ids = [...new Set([WIP_SHELL, CRAFT_PROGRESS, STATUS_CRAFTING, upper(itemField(recipe, 'item_id')), ...refs.map(upper), ...extras])];
  for (let i = 0; i < ids.length; i += 32) await fetchObjectsBatch(ids.slice(i, i + 32));
}

interface CraftingFacts { actorId?: string; encounterId?: string; definitionIds?: readonly string[] }
/** Shared with the sheet through a bridge: the websocket bundle owns caster reads and their superseded-snapshot retry. */
async function hydrateCraftingFacts(facts: CraftingFacts): Promise<void> {
  const ids = [...new Set(facts.definitionIds?.map(upper).filter(isUuid))].slice(0, 32);
  await Promise.allSettled([
    (async () => {
      if (facts.encounterId) await window.ftResyncEncounter?.(facts.encounterId);
      if (facts.actorId) await withSettledCasterReads(() => fetchAndCacheCaster(facts.actorId!, { skipEncounterSeed: true, throwOnError: true }));
    })(),
    ids.length ? fetchObjectsBatch(ids) : Promise.resolve(),
  ]);
}
Object.assign(window, { ftHydrateCraftingFacts: hydrateCraftingFacts });

async function holdCraftStartFacts(input: CraftStartRequest, encounterId: string, snapshot: () => { actor: Row | null; encounter: Row | null }): Promise<void> {
  const missing = new Set<string>();
  const resolve = () => {
    missing.clear();
    const { actor, encounter } = snapshot();
    if (!actor) throw new HandBack('crafting:carried-tree-not-held');
    const read = abilityTruthReaders(actor, encounter ?? {}, new Set());
    const readers: CraftStartReaders = { ...read, entries: read.entries!, inventoryField: read.inventoryField!, resolveItem: entry => {
      const definition = read.resolveItem?.(entry) ?? null;
      const id = upper(readLayerListEntryUuid(entry));
      if (!definition && isUuid(id)) missing.add(id);
      return definition;
    } };
    return resolveCraftStart({ caster: actor, recipe: input.recipe, encounter, requestedSize: input.sizeId }, readers);
  };
  let verdict = resolve();
  if (verdict.kind === 'handback') {
    let facts: CraftingFacts | undefined;
    switch (verdict.reason) {
      case 'crafting_start:encounter-not-held': facts = { encounterId }; break;
      case 'crafting_start:skills-not-held': facts = { actorId: input.actorId }; break;
      case 'crafting_start:reagent-stock-not-held': {
        const { actor, encounter } = snapshot();
        facts = { actorId: input.actorId,
          encounterId: Array.isArray(actor?.inventory) || !Array.isArray(encounter?.encounter_inventories_arr ?? actor?.encounter_inventory) ? encounterId : undefined };
        break;
      }
      case 'crafting_start:plan-output-not-held':
      case 'crafting_start:waiver-provider-not-held':
      case 'crafting_start:reagent-name-not-held': facts = { definitionIds: [...missing] }; break;
    }
    if (facts) {
      await hydrateCraftingFacts(facts);
      verdict = resolve();
    }
  }
  if (verdict.kind === 'handback') throw new HandBack(verdict.reason);
  if (verdict.kind === 'refuse') throw new Refusal(verdict.message);
}

/** Work/Stop and clock boundaries read exactly the same persisted tracker, crew and output definitions. */
async function craftSnapshotFor(h: Awaited<ReturnType<typeof held>>, actorId: string, target: { inventoryId?: string; statusId?: string }) {
  const response = await fetch('/options/api/activity_state_inc.php', { method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ schema: 'janus.activity.truth.v1', operation_id: crypto.randomUUID().toUpperCase(), actor_id: upper(actorId), encounter_id: h.encounterId,
      expected_rev: 0, inventory_id: target.inventoryId ?? '', status_id: target.statusId ?? '' }), signal: AbortSignal.timeout(15000) });
  const raw = record(await response.json());
  if (!response.ok || raw?.ok !== true || !record(raw.tracker) || !record(raw.inventory) || !Array.isArray(raw.workers) || !Array.isArray(raw.connection_ids))
    throw new HandBack(String(raw?.error ?? 'crafting:snapshot-not-held'));
  const snapshot = raw as unknown as CraftSnapshot;
  const recipe = isSelfStatusOrigin(upper(statusScalar(snapshot.tracker, 'status_id')), upper(statusScalar(snapshot.tracker, 'status_origin_id')))
    ? null : record(await fetchAndCacheObject(upper(statusScalar(snapshot.tracker, 'status_origin_id'))));
  if (!recipe) throw new HandBack('crafting:recipe-not-held');
  const payload = String(statusScalar(snapshot.tracker, 'status_casting_targets_arr') ?? '');
  await hydrate(recipe, (payload.match(/[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}/gi) ?? []).map(upper));
  await hydrateReferencedDefinitions(h.actor, h.encounter, [recipe]);
  // Resolving snapshots can name workers who have since stopped; retain them for roller selection.
  for (const worker of snapshot.workers) {
    const id = upper(statusScalar(worker, 'status_target_id'));
    if (!h.ctx.members.has(id)) {
      const peer = record(await fetchAndCacheObject(id));
      if (peer && ecId(peer) === id) { h.ctx.members.set(id, peer); h.ctx.originals = new Map([...h.ctx.originals, [id, peer]]); }
    }
  }
  return { snapshot, recipe };
}

export interface CraftStartRequest { actorId: string; encounterId?: string; recipe: Row; sizeId?: string | null; rollPrivate?: number;
  gate: (encounterId: string) => Promise<{ kind: 'refuse'; message: string } | { kind: 'allow' } | { kind: 'unknown'; site: string }> }
export async function startCraft(input: CraftStartRequest): Promise<{ outcome: 'handled' | 'refused' }> {
  return runRowOnly(input.actorId, async () => {
    try {
      const h = await held(input.actorId, input.encounterId, input);
      const prior = await loadActivityStatus(h.encounterId, input.actorId, STATUS_CRAFTING);
      const plan = decideActivity(h.ctx, ctx => planCraftStart(ctx, h.actor, input.recipe, h.now, input.sizeId, prior));
      plan.rollPrivate = input.rollPrivate;
      await saveActivity(h.encounterId, input.actorId, 'crafting_start', plan);
      return { outcome: 'handled' };
    } catch (error) { activityError(error); return { outcome: 'refused' }; }
  });
}

export interface CraftMembershipRequest { action: 'work' | 'stop'; workerId: string; inventoryId?: string; statusId?: string; encounterId?: string }
export async function changeCraftMembership(input: CraftMembershipRequest): Promise<{ ok: boolean; error?: string }> {
  return runRowOnly(input.workerId, async () => {
    try {
      const h = await held(input.workerId, input.encounterId);
      const { snapshot, recipe } = await craftSnapshotFor(h, input.workerId, input);
      const tools = new Set(requiredToolIds(h.ctx.read.entries?.(recipe, 'requires_tool') ?? [])), roots = new Set<string>();
      for (const { row, holders } of walkRows(h.encounter.encounter_inventories_arr)) if (tools.has(templateIdOf(row))) roots.add(holders[0] ?? inventoryIdOf(row));
      const reach = new Map<string, boolean | null>();
      await Promise.all([...roots].map(async id => { reach.set(id, await actorCanReachTarget(input.workerId, id)); }));
      const toolRead = { itemType: (row: Row) => String(itemField(row, 'item_type') ?? itemField(h.ctx.read.definition(templateIdOf(row)), 'item_type') ?? ''),
        reachable: (id: string) => reach.get(upper(id)) ?? null };
      const plan = decideActivity(h.ctx, ctx => planCraftMembership(ctx, h.actor, recipe, snapshot, h.now, input.action, toolRead, input.statusId));
      await saveActivity(h.encounterId, input.workerId, 'crafting_' + input.action, plan);
      return { ok: true };
    } catch (error) { return activityError(error); }
  });
}

const craftSweeps = new Map<string, Promise<'handled' | 'unresolved'>>();
const craftSkips = new Map<string, string>();
interface CraftWorker { actorId: string; statusId: string; skipReason?: string }
const craftPending = new Map<string, Map<string, CraftWorker>>();
/** The clock's pass-2 observer. Presence of a campaign tracker is not a due effect; a held worker is.
 * Activity receipts recover an uncertain save before another snapshot or any new completion dice. */
export function settleObservedCrafts(input: { encounterId: string; clock: CampaignClockCols }): Promise<'handled' | 'unresolved'> {
  const encounterId = upper(input.encounterId), prior = craftSweeps.get(encounterId) ?? Promise.resolve('handled' as const);
  const next = prior.then(async (): Promise<'handled' | 'unresolved'> => {
    if (window.FateTide?.user?.isGM !== true) return 'handled';
    const encounter = record(actorEncounterFor(encounterId));
    const rows = Array.isArray(encounter?.encounter_creatures_arr) ? encounter.encounter_creatures_arr as Row[] : [];
    const pending = craftPending.get(encounterId) ?? new Map<string, CraftWorker>();
    craftPending.set(encounterId, pending);
    const crew = new Map(pending);
    const payloadEnv = activityPortEnv({ check_uuid: isUuid });
    for (const row of rows) for (const status of statusEntries(row, 'target')) {
      if (statusItemOf(status) !== STATUS_CRAFTING || Number(statusScalar(status, 'status_deleted') ?? 0) !== 0) continue;
      const worker = ftCraftingWorkerPayloadParse(statusScalar(status, 'status_casting_targets_arr'), payloadEnv);
      const trackerId = upper(statusScalar(status, 'status_connected_id'));
      if (worker && isUuid(trackerId) && !crew.has(trackerId)) crew.set(trackerId, { actorId: ecId(row), statusId: upper(statusScalar(status, 'status_id')) });
    }
    // A legacy settle-and-stop can leave a resolving tracker with no live worker. Its
    // saved caster still supplies the observer/actor; the snapshot retains the released crew.
    for (const inventory of [...rows.flatMap(row => [...walkRows(row.inventory)].map(hit => hit.row)),
      ...[...walkRows(encounter?.encounter_inventories_arr)].map(hit => hit.row)]) {
      for (const status of [...statusEntries(inventory, 'target'), ...statusEntries(inventory, 'caster')]) {
        if (statusItemOf(status) !== CRAFT_PROGRESS || Number(statusScalar(status, 'status_deleted') ?? 0) !== 0) continue;
        const progress = ftCraftingProgressPayloadParse(statusScalar(status, 'status_casting_targets_arr'), payloadEnv);
        const trackerId = upper(statusScalar(status, 'status_id')), actorId = upper(statusScalar(status, 'status_caster_id'));
        if (progress?.state === 'resolving' && isUuid(trackerId) && rows.some(row => ecId(row) === actorId) && !crew.has(trackerId))
          crew.set(trackerId, { actorId, statusId: trackerId });
      }
    }
    const readErrors = new Map<string, unknown>();
    if (crew.size) await ensureActivityMembers(encounterId, [...crew.values()].map(worker => worker.actorId), readErrors);
    let unresolved = false;
    for (const [trackerId, worker] of crew) {
      try {
        if (readErrors.has(worker.actorId)) throw readErrors.get(worker.actorId);
        await runRowOnly(worker.actorId, async () => {
          const h = await held(worker.actorId, encounterId, undefined, true);
          if (h.ctx.read.inCombat(h.encounter, h.actor)) return;
          const recovered = pending.get(trackerId);
          if (recovered?.skipReason) { craftSkips.set(trackerId, recovered.skipReason); pending.delete(trackerId); }
          // recoverActivity in held() has proved an uncertain save. A completed/stopped
          // worker can already be absent from the fresh roster; do not read a deleted WIP.
          if (recovered && !statusEntries(h.actor, 'target').some(status => upper(statusScalar(status, 'status_id')) === worker.statusId
            && Number(statusScalar(status, 'status_deleted') ?? 0) === 0)) { pending.delete(trackerId); return; }
          const { snapshot, recipe } = await craftSnapshotFor(h, worker.actorId, worker);
          const through = ftCampaignNowSeconds(input.clock, h.calendar);
          let plan;
          try { plan = decideActivity(h.ctx, ctx => planCraftHeartbeat(ctx, h.actor, recipe, snapshot, through)); }
          catch (error) {
            if (!(error instanceof HandBack)) throw error;
            if (craftSkips.get(trackerId) === error.reason) return;
            const name = String(ecLean(h.actor, 'encounter_creature_name') || itemField(h.actor, 'item_name') || 'Crafting worker');
            plan = { writes: [], rows: [skipUnported(`time-push:crafting:${name}:${error.reason}`)], expectedRev: h.ctx.activityExpectedRev };
            pending.set(trackerId, { ...worker, skipReason: error.reason });
            await saveActivity(encounterId, worker.actorId, 'crafting_tick', plan);
            pending.delete(trackerId);
            craftSkips.set(trackerId, error.reason);
            return;
          }
          if (plan.writes.length || plan.rows.length) {
            pending.set(trackerId, worker);
            await saveActivity(encounterId, worker.actorId, 'crafting_tick', plan);
            pending.delete(trackerId);
          }
          craftSkips.delete(trackerId);
        });
      } catch { unresolved = true; }
    }
    return unresolved ? 'unresolved' : 'handled';
  }).catch(() => 'unresolved' as const);
  craftSweeps.set(encounterId, next);
  void next.finally(() => { if (craftSweeps.get(encounterId) === next) craftSweeps.delete(encounterId); });
  return next;
}

// Both aggregates can include this module. All time senders use the first page-owned
// observer so its queued work, undecided answers and activity receipts share one owner.
const craftHost = window as unknown as { ftSettleObservedCrafts?: typeof settleObservedCrafts };
craftHost.ftSettleObservedCrafts ??= settleObservedCrafts;
